/**
 * Station alerts (the station workspaces plan, step 7; migration 0209; the Go side is platform-core/internal/twin/
 * stations_alerts*.go): a background loop judges each staffed station's levels every two minutes and raises an alert
 * when a watched tag's live value is outside the plant's window (warn) or past a stop limit (stop), after two
 * evaluations out (a stop at once); it clears after two back in, past a deadband. Sheet-fed plants get levels, never
 * alerts.
 *
 *   GET  /v1/twin/station-alerts?state=open|all&mine=1&station_id=&count=1
 *                                                                    {items, open, mine_open, mine_unacked}; "mine" is
 *                                                                    the stations the caller belongs to, every one for
 *                                                                    an org admin; count=1 answers the counts alone
 *   POST /v1/twin/station-alerts/{alert_id}/ack                      {kind, detail?}: a member of the station or an org
 *                                                                    admin; changed and maintenance write a tried
 *                                                                    event on the Actions list
 *   GET  /v1/twin/settings/alerts                                    {escalation_minutes, email_enabled}; any member
 *   PUT  /v1/twin/settings/alerts                                    {escalation_minutes} 5 to 240: an org admin
 *
 * A vendor overlay, or an org without the foundry pack, is refused the read and sees nothing, quietly. The header's
 * bell reads mine_unacked every minute (the counts alone): an acknowledged alert has someone on it. It stops for a
 * caller the read is refused to.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";
import { useOrg } from "@/lib/org-context";
import { errorCodeOf, isProductRefused } from "./error";
import { ACTIONS_KEY } from "./actions";
import { retryBoardRead } from "./station-board";

export type StationAlertState = "open" | "acknowledged" | "cleared";
export type StationAlertAckKind = "changed" | "checked_ok" | "reading_wrong" | "maintenance" | "other";

export interface StationAlert {
  id: string;
  station_id: string;
  station_name: string;
  /** Where it was raised from: the levels (a hand-made alarm rule on a station later). */
  source: "level" | "rule";
  condition_key: string;
  label: string;
  entity_id: string;
  tag_id: string;
  tag_name: string;
  level: "warn" | "stop";
  /** The side of the window (or of the stop limit) the value is out on; absent before step 7c. */
  side?: "below" | "above";
  state: StationAlertState;
  /** The latest value and the band, in `unit` (the org's display unit when it was judged). */
  value: number | null;
  unit: string;
  band: { lo?: number; hi?: number; stop_lo?: number; stop_hi?: number };
  at_risk?: string;
  raised_at: string;
  last_seen_at: string;
  out_for_min: number;
  /** No live value since: the alert stays open, never cleared without a reading back inside. */
  stale_since: string | null;
  cleared_at: string | null;
  /** back_in (two evaluations back inside) or unwatched (the station no longer watches its condition). */
  cleared_reason?: "back_in" | "unwatched" | "";
  reraised: number;
  acked_at: string | null;
  acked_by_name?: string;
  ack_kind?: StationAlertAckKind | "";
  ack_note?: string;
  /** How far it has stepped up unacknowledged: 0, 1 (the station's leads), 2 (the org's admins). */
  escalation: number;
  can_ack: boolean;
}

export interface StationAlertList {
  items: StationAlert[];
  /** Open (or acknowledged, not cleared) alerts in the org, and at the caller's stations: the header's badge. */
  open: number;
  mine_open: number;
  /** Those at the caller's stations not yet acknowledged (open, including one open again): the header's bell. */
  mine_unacked: number;
}

export interface StationAlertsQuery {
  state?: "open" | "all";
  mine?: boolean;
  stationId?: string;
  /** The counts alone: items [], no item read. */
  count?: boolean;
}

export interface StationAlertAck {
  kind: StationAlertAckKind;
  /** What was done; required for "other", at most 1800 characters. */
  detail?: string;
}

/** A list read's query string: only what is asked. */
export function stationAlertsSearch(q: StationAlertsQuery): string {
  const p = new URLSearchParams();
  if (q.state) p.set("state", q.state);
  if (q.mine) p.set("mine", "1");
  if (q.stationId) p.set("station_id", q.stationId);
  if (q.count) p.set("count", "1");
  const s = p.toString();
  return s ? `?${s}` : "";
}

export const stationAlertsApi = {
  list: (q: StationAlertsQuery) => api.get<StationAlertList>(`/v1/twin/station-alerts${stationAlertsSearch(q)}`),
  ack: (id: string, body: StationAlertAck) =>
    api.post<StationAlert>(`/v1/twin/station-alerts/${encodeURIComponent(id)}/ack`, body),
};

export const STATION_ALERTS_KEY = ["station-alerts"] as const;
export const stationAlertsKey = (q: StationAlertsQuery) => ["station-alerts", "list", stationAlertsSearch(q)] as const;
export const STATION_ALERTS_BADGE_KEY = ["station-alerts", "badge"] as const;

/** How often an open list and the header's badge are read again. */
export const STATION_ALERTS_REFRESH_MS = 60_000;

/** True when the alerts are refused to this caller for good: a vendor overlay, an org without the foundry pack, or an
 *  org whose Foundry Twin is not active (the product gate). */
export function stationAlertsHidden(err: unknown): boolean {
  const code = errorCodeOf(err);
  return code === "vendor_no_twin" || code === "pack_not_enabled" || isProductRefused(err);
}

const isLiveApiFlag = import.meta.env.VITE_USE_LIVE_API === "true";

function useAlertsLive(): boolean {
  const { isAuthenticated } = useAuth();
  const { activeOrgId } = useOrg();
  return isLiveApiFlag && isAuthenticated && activeOrgId !== null;
}

/** A list of station alerts, read again every minute while it is open. */
export function useStationAlertsQuery(q: StationAlertsQuery, enabled = true) {
  const live = useAlertsLive();
  return useQuery({
    queryKey: stationAlertsKey(q),
    queryFn: () => stationAlertsApi.list(q),
    enabled: live && enabled,
    staleTime: 30_000,
    retry: retryBoardRead,
    refetchInterval: (query) => (stationAlertsHidden(query.state.error) ? false : STATION_ALERTS_REFRESH_MS),
  });
}

/**
 * The header's bell: the alerts at the caller's stations not yet acknowledged, the counts alone read every minute; never
 * for a caller refused.
 */
export function useMyUnackedAlertsQuery() {
  const live = useAlertsLive();
  return useQuery({
    queryKey: STATION_ALERTS_BADGE_KEY,
    queryFn: () => stationAlertsApi.list({ state: "open", mine: true, count: true }),
    enabled: live,
    staleTime: 30_000,
    retry: false,
    select: (d) => d.mine_unacked,
    refetchInterval: (query) => (stationAlertsHidden(query.state.error) ? false : STATION_ALERTS_REFRESH_MS),
  });
}

/**
 * Acknowledge an alert with what was done. Every list and the badge are read again, and the Actions list (a change or
 * maintenance is a tried event there).
 */
export function useAckStationAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: StationAlertAck }) => stationAlertsApi.ack(id, body),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: STATION_ALERTS_KEY });
      void qc.invalidateQueries({ queryKey: ACTIONS_KEY });
    },
  });
}

// ── The org's alert settings (step 7c) ──

export interface AlertSettings {
  /**
   * How long an open alert waits for an acknowledgement before it steps up to the station's leads; twice that, to the
   * org's admins. 5 to 240.
   */
  escalation_minutes: number;
  /** Whether the platform emails station alerts at all (STATION_ALERT_EMAIL_ENABLED): read-only. */
  email_enabled: boolean;
}

export const ALERT_SETTINGS_MIN = 5;
export const ALERT_SETTINGS_MAX = 240;
export const ALERT_SETTINGS_KEY = ["station-alerts", "settings"] as const;

export const alertSettingsApi = {
  get: () => api.get<AlertSettings>("/v1/twin/settings/alerts"),
  put: (escalationMinutes: number) =>
    api.put<AlertSettings>("/v1/twin/settings/alerts", { escalation_minutes: escalationMinutes }),
};

/** The org's alert settings: any member reads them. */
export function useAlertSettingsQuery() {
  const live = useAlertsLive();
  return useQuery({
    queryKey: ALERT_SETTINGS_KEY,
    queryFn: alertSettingsApi.get,
    enabled: live,
    staleTime: 60_000,
    retry: retryBoardRead,
  });
}

/** Set the escalation minutes (an org admin): the answer is the settings as saved. */
export function useSaveAlertSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (escalationMinutes: number) => alertSettingsApi.put(escalationMinutes),
    onSuccess: (saved) => qc.setQueryData(ALERT_SETTINGS_KEY, saved),
    onSettled: () => void qc.invalidateQueries({ queryKey: ALERT_SETTINGS_KEY }),
  });
}
